import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'design-system';
import { TriangleAlert } from 'lucide-react';

type SaveErrorVariant = 'new' | 'rename' | 'save';

type SaveErrorDialogProps = {
  open: boolean;
  isNew: boolean;
  isRename: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onRetry: () => void;
};

const getVariant = (isNew: boolean, isRename: boolean): SaveErrorVariant => {
  if (isNew) return 'new';
  if (isRename) return 'rename';
  return 'save';
};

const getTitle = (variant: SaveErrorVariant): string => {
  switch (variant) {
    case 'new':
      return "Dashboard couldn't be created";
    case 'rename':
      return "Rename couldn't be saved";
    case 'save':
      return "Changes couldn't be saved";
  }
};

const getDescription = (variant: SaveErrorVariant) => {
  switch (variant) {
    case 'new':
      return (
        <>
          <span className='block'>Something went wrong.</span>
          <span className='block'>Try again or cancel.</span>
        </>
      );
    case 'rename':
      return (
        <>
          <span className='block'>Something went wrong.</span>
          <span className='block'>
            The dashboard has been restored to its previous name and URL.
          </span>
        </>
      );
    case 'save':
      return (
        <>
          <span className='block'>Something went wrong. Your draft is still here.</span>
          <span className='block'>Retry or discard to restore to last saved version.</span>
        </>
      );
  }
};

export const SaveErrorDialog = ({
  open,
  isNew,
  isRename,
  onCancel,
  onDiscard,
  onRetry
}: SaveErrorDialogProps) => {
  const variant = getVariant(isNew, isRename);

  return (
    <Dialog
      disablePointerDismissal
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent
        className='sm:max-w-[560px]'
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <TriangleAlert
              aria-hidden='true'
              className='size-5 text-destructive'
            />
            {getTitle(variant)}
          </DialogTitle>
          <DialogDescription className='space-y-1'>{getDescription(variant)}</DialogDescription>
        </DialogHeader>
        <DialogFooter className='grid grid-cols-1 sm:grid-cols-3'>
          <Button
            className='w-full'
            variant='outline'
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            className='w-full'
            variant='outline'
            onClick={onRetry}
          >
            {variant === 'new' ? 'Try again' : 'Retry'}
          </Button>
          {variant !== 'new' && (
            <Button
              className='w-full'
              variant='destructive'
              onClick={onDiscard}
            >
              Discard changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
